import { type ChangeEvent, type KeyboardEvent, useCallback, useMemo, useRef, useState } from "react";

import type { ClickableOption, MultiSelectOption } from "common/components/form/MultiSelect/types";
import { useOutsideClickDetection } from "common/utilities";

type UseMultiSelectParams<T> = {
  values?: MultiSelectOption<T>[];
  options: ClickableOption<T>[];
  removeOption: (option: MultiSelectOption<T>) => void;
};

export const useMultiSelect = <T>({ values, options, removeOption }: UseMultiSelectParams<T>) => {
  const [input, setInput] = useState<string>("");
  const [areOptionsVisible, setAreOptionsVisible] = useState<boolean>(false);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const resultsContainerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useOutsideClickDetection(resultsContainerRef, () => setAreOptionsVisible(false));

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => setInput(event.target.value);

  const filteredOptionsByInput = useMemo(
    () => options.filter((option) => option.label.toLowerCase().includes(input.toLowerCase())),
    [input, options],
  );

  const selectOption = useCallback((option: ClickableOption<T>) => {
    option.onClick();
    setAreOptionsVisible(false);
    setInput("");
    inputRef.current?.focus();
  }, []);

  const openSelectOptionsList = () => setAreOptionsVisible(true);
  const closeSelectOptionsList = () => setAreOptionsVisible(false);

  const closeSelectOptionsListOnKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Escape") {
      event.stopPropagation();
      closeSelectOptionsList();
    }
  };

  const handleInputKeyPress = (event: KeyboardEvent) => {
    if (event.key === "Backspace" && input === "" && values && values.length > 0) {
      removeOption(values[values.length - 1]);
      return;
    }

    if (event.key === "Enter" && filteredOptionsByInput.length > 0) {
      event.preventDefault();
      event.stopPropagation();
      selectOption(filteredOptionsByInput[0]);
      return;
    }
  };

  return {
    containerRef,
    inputRef,
    resultsContainerRef,
    input,
    filteredOptionsByInput,
    areOptionsVisible,
    handleInputChange,
    openSelectOptionsList,
    closeSelectOptionsListOnKeyDown,
    selectOption,
    handleInputKeyPress,
  };
};
